import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { auditAPI } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { MagnifyingGlass, ClipboardText } from '@phosphor-icons/react';

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await auditAPI.list({ limit: 200 });
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const filteredLogs = logs.filter(log => {
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        log.action?.toLowerCase().includes(searchLower) ||
        log.actor_name?.toLowerCase().includes(searchLower) ||
        log.module?.toLowerCase().includes(searchLower) ||
        log.actor_type?.toLowerCase().includes(searchLower) ||
        JSON.stringify(log.details)?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const getActionBadgeColor = (action) => {
    const a = (action || '').toLowerCase();
    if (a.startsWith('post') || a.includes('created')) return 'bg-green-100 text-green-700';
    if (a.startsWith('put') || a.startsWith('patch') || a.includes('updated') || a.includes('changed')) return 'bg-blue-100 text-blue-700';
    if (a.startsWith('delete') || a.includes('deleted')) return 'bg-red-100 text-red-700';
    if (a.includes('login')) return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div data-testid="audit-logs-page">
      <PageHeader 
        title="Audit Logs" 
        subtitle={`${total} total events`}
      />
      
      <div className="page-content">
        {/* Search */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input
              placeholder="Search audit logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-logs"
            />
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Module</th>
                <th>Action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(5)].map((_, j) => (
                      <td key={j}><div className="skeleton h-4 w-24 rounded"></div></td>
                    ))}
                  </tr>
                ))
              ) : filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <tr key={log.id} data-testid={`log-row-${log.id}`}>
                    <td className="text-sm">{formatDateTime(log.timestamp)}</td>
                    <td className="text-sm">
                      <div className="font-medium text-[#0F172A]">{log.actor_name || log.user_id?.slice(0, 8) || 'system'}</div>
                      {log.actor_type && <div className="text-xs text-[#64748B] capitalize">{log.actor_type}</div>}
                    </td>
                    <td className="text-xs text-[#64748B] capitalize">{(log.module || '—').replace(/_/g, ' ')}</td>
                    <td>
                      <span className={`text-xs px-2 py-1 rounded-full ${getActionBadgeColor(log.action)}`}>
                        {log.action?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="text-sm text-[#64748B] max-w-md truncate">
                      {JSON.stringify(log.details)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-[#64748B]">
                    <ClipboardText size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No audit logs found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
